/**
 * @module @yimoka/i18n/create-app-i18n
 * @summary 主工厂：组装 i18n / backend / sync
 * @author keep
 * @since 2026-05-19
 */

import i18next from 'i18next';

import { createBundledRegistry } from './bundled/registry';
import { createI18nBackend } from './remote/backend';
import { createSyncManager } from './sync/manager';
import { resolveSyncPolicy } from './sync/policy';
import { createTypedT } from './typed-t';
import type { AppI18nInstance, BundledMap, CreateAppI18nOptions, RemoteSharedState } from './types';

/**
 * 创建应用 i18n 实例（不调用 init，由 WatchLang 或调用方 init）。
 *
 * @param options - 配置
 * @returns AppI18nInstance
 */
export function createAppI18n<TBundled extends BundledMap>(
  options: CreateAppI18nOptions<TBundled>,
): AppI18nInstance<TBundled> {
  const {
    bundled,
    fetchRemote,
    sync: syncPartial,
    storage,
    onLoadingChange,
    subscribeAppResume,
    debug = false,
  } = options;

  const defaultLng = (options.defaultLng ?? Object.keys(bundled)[0]) as keyof TBundled & string;
  const supportedLngs = options.supportedLngs ?? Object.keys(bundled);
  const policy = resolveSyncPolicy(syncPartial);
  const registry = createBundledRegistry(bundled, defaultLng);

  const remoteState: RemoteSharedState = {
    memoryCache: new Map(),
    inflight: new Map(),
    failedLangs: new Set(),
  };

  const i18n = i18next.createInstance();

  const backendModule = fetchRemote
    ? createI18nBackend({
      registry,
      fetchRemote,
      state: remoteState,
      memoryCacheTtlMs: policy.memoryCacheTtlMs,
      onLoadingChange,
    })
    : null;

  const syncManager = fetchRemote
    ? createSyncManager({
      registry,
      fetchRemote,
      storage,
      policy,
      supportedLngs,
      state: remoteState,
      onLoadingChange,
      subscribeAppResume,
    })
    : createNoopSyncManager();

  const resources: Record<string, Record<string, Record<string, unknown>>> = {};
  for (const lang of supportedLngs) {
    resources[lang] = registry.getResourcesForLang(lang);
  }

  function getInitOptions(lng: string): Record<string, unknown> {
    return {
      resources,
      lng,
      fallbackLng: defaultLng,
      supportedLngs,
      // BCP 47 完整码（zh-CN / en-US）：仅加载当前语种，避免 i18next 追加 languageOnly（en / zh）回退链。
      // 勿开 nonExplicitSupportedLngs —— 它会把码剥成 en/zh 再查 supportedLngs，与完整码列表永远对不上。
      load: 'currentOnly',
      // bundled / supportedLngs 使用标准 BCP 47 大小写（zh-CN），禁止 i18next 降成 zh-cn 导致资源键错位。
      lowerCaseLng: false,
      ns: Object.keys(bundled[defaultLng] ?? {}),
      defaultNS: Object.keys(bundled[defaultLng] ?? {})[0] ?? 'translation',
      interpolation: { escapeValue: false },
      debug,
      partialBundledLanguages: true,
      ...(fetchRemote ? { backend: { backends: [] } } : {}),
    };
  }

  const t = createTypedT<TBundled, typeof defaultLng>(
    (key: string, opts?: Record<string, unknown>) => i18n.t(key, opts),
  );

  return {
    i18n,
    t,
    bundled,
    defaultLng,
    supportedLngs,
    syncManager,
    remoteState,
    backendModule,
    getInitOptions,
  };
}

/** 无远程时的空 SyncManager */
function createNoopSyncManager() {
  return {
    start: async () => {},
    stop: () => {},
    refreshLang: async () => {},
  };
}
